import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faNewspaper,
  faBullhorn,
  faCreditCard,
  faExclamationTriangle,
  faShieldAlt,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket,
  faEye,
  faLock,
  faDatabase,
  faChild,
  faCog,
  faLink,
  faInfoCircle,
  faGavel,
  faBan
} from '@fortawesome/free-solid-svg-icons';

const Disclaimer = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const disclaimerSections = [
    {
      id: 1,
      title: "Accuracy of Information",
      icon: faInfoCircle,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      items: [
        "While we make every effort to ensure the accuracy of the content and news shared on our platform, IINSAF does not guarantee that all information is complete, reliable, or up to date.",
        "News and reports are often provided by independent reporters and contributors, and IINSAF is not legally responsible for the correctness or credibility of user-submitted content."
      ]
    },
    {
      id: 2,
      title: "Advertisements",
      icon: faBullhorn,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      items: [
        "Advertisements displayed on IINSAF are published after basic verification.",
        "However, IINSAF is not liable for any claims, losses, or damages arising from reliance on third-party advertisements.",
        "Users and advertisers are solely responsible for their own transactions and dealings."
      ]
    },
    {
      id: 3,
      title: "Third-Party Links",
      icon: faLink,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      items: [
        "Our platform may include links, embeds, or references to third-party websites or services (such as Facebook, YouTube, Instagram).",
        "IINSAF is not responsible for the availability, reliability, or content of such external services, nor for their privacy practices."
      ]
    },
    {
      id: 4,
      title: "Liability Limitation",
      icon: faExclamationTriangle,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      borderColor: "border-red-200",
      items: [
        "IINSAF will not be held responsible for any fraud, scams, nudity, objectionable content, or illegal activities carried out by users.",
        "Any person engaging in such acts will be solely responsible and may face legal consequences under applicable laws.",
        "Users are advised to act responsibly and verify information before taking action based on any content published on the platform."
      ],
      warning: "Users engaging in prohibited activities may face legal consequences."
    },
    {
      id: 5,
      title: "No Legal or Financial Advice",
      icon: faGavel,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      borderColor: "border-orange-200",
      items: [
        "Content published on IINSAF should not be considered legal, financial, or professional advice.",
        "Users must seek independent professional guidance before making any decisions based on information provided on the platform."
      ]
    },
    {
      id: 6,
      title: "Platform Security",
      icon: faShieldAlt,
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      borderColor: "border-indigo-200",
      items: [
        "While we strive to keep the platform safe and secure, we cannot guarantee that the website will always be error-free, uninterrupted, or completely secure.",
        "Users are responsible for protecting their own devices and accounts while accessing the platform."
      ]
    },
    {
      id: 7,
      title: "Governing Law",
      icon: faGavel,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "from-emerald-50 to-emerald-100",
      borderColor: "border-emerald-200",
      items: [
        "This Disclaimer is governed by the laws of India, and any disputes shall be subject to the jurisdiction of Indian courts."
      ]
    },
    {
      id: 8,
      title: "Contact Us",
      icon: faPhone,
      color: "from-cyan-500 to-cyan-600",
      bgColor: "from-cyan-50 to-cyan-100",
      borderColor: "border-cyan-200",
      items: [
        "Phone: 9992396623",
        "Email: iinsafofficial@gmail.com",
        "Website: www.iinsaf.com"
      ]
    }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              DISCLAIMER
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-500 to-purple-600 bg-clip-text text-transparent">
                Disclaimer
              </span>{' '}
              – IINSAF
          </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-purple-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              The information and services provided on{' '}
              <span className="font-semibold text-blue-600">www.iinsaf.com</span> are for general informational and communication purposes only. 
              By using our platform, you agree to the terms of this Disclaimer.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Disclaimer Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {disclaimerSections.map((section, index) => (
            <motion.div
              key={section.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className={`p-6 sm:p-8 bg-gradient-to-r ${section.bgColor} border-b ${section.borderColor}`}>
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-r ${section.color} rounded-xl flex items-center justify-center mr-4 shadow-lg`}>
                    <FontAwesomeIcon icon={section.icon} className="text-white text-xl" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {section.id}. {section.title}
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <FontAwesomeIcon 
                        icon={faCheckCircle} 
                        className={`text-${section.color.split('-')[1]}-500 mt-1 mr-3 flex-shrink-0`} 
                      />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                  
                  {section.warning && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: section.items.length * 0.1 }}
                      className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-3" />
                        <span className="text-red-700 font-semibold">{section.warning}</span>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Questions About Disclaimer?</h2>
          </div>
          <p className="text-white/90 mb-6 text-lg">
            Need clarification on our disclaimer or have legal questions? Our support team is here to help.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:9992396623" className="text-white/90 hover:text-white transition-colors duration-300">
                  9992396623
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                  iinsafofficial@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                  www.iinsaf.com
                </a>
              </div>
            </div>
        </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Disclaimer;